import {
  ITheme,
  defaultDarkTheme,
  defaultTheme,
  teamsDarkTheme,
  teamsTheme,
} from '@ms/yammer-web-core-ui/dist/util/theme';
import {
  Theme as Fluent9Theme,
  teamsDarkTheme as fluent9TeamsDarkTheme,
  teamsLightTheme as fluent9TeamsLightTheme,
  webDarkTheme as fluent9WebDarkTheme,
  webLightTheme as fluent9WebLightTheme,
} from '@ms/yammer-web-core-ui/dist/util/theme/fluent9';
import { getTeamsTabContentId, getTeamsTabContentType } from 'domains/teamsTab/selectors';

import {
  getDeepLinkPath,
  getEntityId,
  getMetaOsHubName,
  getTeamsCachedAppSwitchContext,
  getTeamsContext,
  getTheme,
  isMobileClient,
  isTeamsCached,
  isTeamsHub,
  isTeamsPersonalApp,
  isTeamsTab,
  isWebClient,
} from '../domains/teams/selectors';

import { useSelector } from './useSelector';

type UseTeamsContext = () => ReturnType<typeof getTeamsContext>;
export const useTeamsContext: UseTeamsContext = () => useSelector(getTeamsContext);

type UseTeamsCachedAppSwitchContext = () => ReturnType<typeof getTeamsCachedAppSwitchContext>;
export const useTeamsCachedAppSwitchContext: UseTeamsCachedAppSwitchContext = () =>
  useSelector(getTeamsCachedAppSwitchContext);

type UseIsTeamsContrastTheme = () => boolean;
export const useIsTeamsContrastTheme: UseIsTeamsContrastTheme = () => {
  const theme = useSelector(getTheme);

  return theme === 'contrast';
};

type UseTheme = () => ITheme;
export const useTheme: UseTheme = () => {
  const theme = useSelector(getTheme);

  return theme === 'dark' ? defaultDarkTheme : defaultTheme;
};

type UseTeamsTheme = () => ITheme;
export const useTeamsTheme: UseTeamsTheme = () => {
  const theme = useSelector(getTheme);

  return theme === 'dark' ? teamsDarkTheme : teamsTheme;
};

type UseFluent9Theme = () => Fluent9Theme;
export const useFluent9Theme: UseFluent9Theme = () => {
  const theme = useSelector(getTheme);

  return theme === 'dark' ? fluent9WebDarkTheme : fluent9WebLightTheme;
};

type UseFluent9TeamsTheme = () => Fluent9Theme;
export const useFluent9TeamsTheme: UseFluent9TeamsTheme = () => {
  const theme = useSelector(getTheme);

  return theme === 'dark' ? fluent9TeamsDarkTheme : fluent9TeamsLightTheme;
};

type UseIsMobileClient = () => ReturnType<typeof isMobileClient>;
export const useIsMobileClient: UseIsMobileClient = () => useSelector(isMobileClient);

type UseIsWebClient = () => ReturnType<typeof isWebClient>;
export const useIsWebClient: UseIsWebClient = () => useSelector(isWebClient);

type UseTeamsTabContentType = () => ReturnType<typeof getTeamsTabContentType>;
export const useTeamsTabContentType: UseTeamsTabContentType = () => useSelector(getTeamsTabContentType);

type UseTeamsTabContentId = () => ReturnType<typeof getTeamsTabContentId>;
export const useTeamsTabContentId: UseTeamsTabContentId = () => useSelector(getTeamsTabContentId);

type UseIsTeamsTab = () => ReturnType<typeof isTeamsTab>;
export const useIsTeamsTab: UseIsTeamsTab = () => useSelector(isTeamsTab);

type UseDeepLinkPath = () => ReturnType<typeof getDeepLinkPath>;
export const useDeepLinkPath: UseDeepLinkPath = () => useSelector(getDeepLinkPath);

type UseGetMetaOsHubName = () => ReturnType<typeof getMetaOsHubName>;
export const useGetMetaOsHubName: UseGetMetaOsHubName = () => useSelector(getMetaOsHubName);

type UseIsTeamsHub = () => ReturnType<typeof isTeamsHub>;
export const useIsTeamsHub: UseIsTeamsHub = () => useSelector(isTeamsHub);

type UseIsTeamsPersonalApp = () => ReturnType<typeof isTeamsPersonalApp>;
export const useIsTeamsPersonalApp: UseIsTeamsPersonalApp = () => useSelector(isTeamsPersonalApp);

type UseGetEntityId = () => ReturnType<typeof getEntityId>;
export const useGetEntityId: UseGetEntityId = () => useSelector(getEntityId);

type UseIsTeamsCached = () => ReturnType<typeof isTeamsCached>;
export const useIsTeamsCached: UseIsTeamsCached = () => useSelector(isTeamsCached);
